import React from 'react';
import { Oval as Loader } from 'react-loader-spinner';
import { MESSAGE_CONSTANTS } from '../constants/message';


function formatTipAmount(amount) {
  const num = Number(amount);
  if (!Number.isFinite(num)) return null;
  return num === Math.trunc(num) ? Math.trunc(num) : num.toFixed(2);
}

/**
 * True when the logged-in user is a creator in a 1:1 chat (messaging a subscriber/follower).
 * By default, true when the creator has sent more than one message in the thread.
 * @param {boolean} isCreator
 * @param {Array} threadItems
 * @param {{ maxThreadLength?: number, maxMyMessageCount?: number }} [options]
 * @returns {boolean}
 */
export function isCreatorMessagingOtherUser(isCreator, threadItems, options = {}) {
  const { maxThreadLength, maxMyMessageCount } = options;
  const threadInRange =
    threadItems.length > 1 &&
    (maxThreadLength == null || threadItems.length <= maxThreadLength);
  if (!isCreator || !threadInRange) return false;

  const myMessageItems = threadItems.filter(
    (ti) => ti.type === 'message' && ti.item?.isMine
  );

  if (maxMyMessageCount != null) {
    return myMessageItems.length <= maxMyMessageCount;
  }
  return myMessageItems.length > 1;
}

/** USD tip total from a message-tip JSON payload (matches {@link buildCallTipText} creator field order). Not platform credits — do not apply credits→USD conversion. */
export function tipMetaUsdForCreator(tipMeta) {
  if (!tipMeta || typeof tipMeta !== 'object') return 0;
  const raw = tipMeta.realPrice ?? tipMeta.baseAmount ?? tipMeta.amount;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function buildCallTipText({ tipMeta, isCreator, opponentDisplayName }) {
  const rawAmount = isCreator
    ? tipMeta?.realPrice ?? tipMeta?.baseAmount ?? tipMeta?.amount
    : tipMeta?.creditAmount ?? tipMeta?.amount ?? tipMeta?.baseAmount ?? tipMeta?.realPrice;
  const amountLabel = formatTipAmount(rawAmount);

  if (isCreator) {
    const handle = String(opponentDisplayName || 'subscriber').trim();
    const prefixedHandle = handle.startsWith('@') ? handle : `@${handle}`;
    // return amountLabel
    //   ? `${prefixedHandle} just tipped you ${amountLabel} credits`
    //   : `${prefixedHandle} just tipped you`;
    return `${prefixedHandle} just tipped you`;
  }

  return amountLabel ? `You just tipped ${amountLabel} credits` : 'You just tipped';
}

/**
 * Safely convert a value to a string, returning empty string if not a string
 * @param {*} value - Value to convert to string
 * @returns {string} String value or empty string
 */
export function safeString(value) {
  return typeof value === 'string' ? value : '';
}

/** True if `value` looks like a MongoDB ObjectId string (24 hex chars). Used to avoid navigating to `/chat/:username` before backend room metadata loads. */
export function isMongoObjectIdString(value) {
  return typeof value === 'string' && /^[a-f\d]{24}$/i.test(value);
}

/**
 * Extract rooms array from matrixRooms object
 * @param {Object|Array} matrixRooms - Matrix rooms object or array
 * @returns {Array} Array of rooms, or empty array if invalid
 */
export function getMatrixRoomsArray(matrixRooms) {
  if (!matrixRooms) return [];
  if (Array.isArray(matrixRooms.rooms)) return matrixRooms.rooms;
  return [];
}

/** Upsert backend room rows by `roomid` (used when refreshing page 1 metadata). */
export function upsertMatrixRoomsByRoomId(existingPayload, incomingPayload) {
  const existingRooms = getMatrixRoomsArray(existingPayload);
  const incomingRooms = getMatrixRoomsArray(incomingPayload);
  const byRoomId = new Map(
    existingRooms
      .filter((room) => room?.roomid)
      .map((room) => [room.roomid, room]),
  );

  incomingRooms.forEach((room) => {
    if (room?.roomid) {
      byRoomId.set(room.roomid, room);
    }
  });

  return {
    ...incomingPayload,
    rooms: Array.from(byRoomId.values()),
  };
}

/** Append unique backend room rows by `roomid`. */
export function appendMatrixRooms(existingPayload, incomingPayload) {
  const existingRooms = getMatrixRoomsArray(existingPayload);
  const incomingRooms = getMatrixRoomsArray(incomingPayload);
  const seen = new Set(existingRooms.map((room) => room?.roomid).filter(Boolean));
  const merged = [...existingRooms];

  incomingRooms.forEach((room) => {
    if (room?.roomid && !seen.has(room.roomid)) {
      merged.push(room);
      seen.add(room.roomid);
    }
  });

  return {
    ...incomingPayload,
    rooms: merged,
  };
}

/**
 * Get the start of the day (midnight) for a given timestamp
 * @param {number|Date} ts - Timestamp or Date object
 * @returns {number} Timestamp of the start of the day
 */
export function getLocalDayStart(ts) {
  const d = new Date(typeof ts === 'number' ? ts : Date.now());
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Check if the scroll container is near the bottom
 * @param {HTMLElement|null} container - The scrollable container element
 * @returns {boolean} True if near bottom, false otherwise
 */
export function isNearBottom(container) {
  if (!container) return true;
  return (
    container.scrollHeight - container.scrollTop - container.clientHeight <
    MESSAGE_CONSTANTS.SCROLL_NEAR_BOTTOM_THRESHOLD_PX
  );
}

/**
 * True when the scroll container is in the top band (user is reading older history).
 * @param {HTMLElement|null} container
 * @param {number} [topThreshold]
 */
export function isAtScrollTop(
  container,
  topThreshold = MESSAGE_CONSTANTS.SCROLL_TOP_THRESHOLD_PX
) {
  if (!container) return false;
  return container.scrollTop <= topThreshold;
}

/**
 * Keep the viewport on the same messages after older rows are prepended above.
 * Uses height-delta anchoring only — never moves scroll unless the DOM grew.
 *
 * @param {HTMLElement|null} container
 * @param {{ scrollHeight: number, scrollTop: number }} before
 */
export function preserveScrollAfterPrepend(container, before) {
  if (!container || !before || before.scrollHeight <= 0) return;

  const delta = container.scrollHeight - before.scrollHeight;
  if (delta === 0) return;

  container.scrollTop = before.scrollTop + delta;
}

/**
 * Get the label for a date separator (Today, Yesterday, or formatted date)
 * @param {number} dayStart - Timestamp of the start of the day
 * @param {Object} options - Options object
 * @param {number} options.todayStart - Timestamp of today's start
 * @param {number} options.yesterdayStart - Timestamp of yesterday's start
 * @returns {string} The date separator label
 */
export function getDateSeparatorLabel(dayStart, { todayStart, yesterdayStart }) {
  if (dayStart === todayStart) return 'Today';
  if (dayStart === yesterdayStart) return 'Yesterday';
  return new Date(dayStart).toLocaleDateString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Build thread items with date separators from messages array
 * @param {Array} messages - Array of message objects
 * @returns {Array} Array of thread items (date separators and messages)
 */
export function buildThreadItems(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return [];

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();

  const out = [];
  let lastDayStart = null;

  for (const msg of messages) {
    const ts = typeof msg?.ts === 'number' ? msg.ts : Date.now();
    const dayStart = getLocalDayStart(ts);

    if (dayStart !== lastDayStart) {
      out.push({
        type: 'date',
        key: `date-${dayStart}`,
        label: getDateSeparatorLabel(dayStart, { todayStart, yesterdayStart }),
      });
      lastDayStart = dayStart;
    }

    out.push({
      type: 'message',
      key: msg?.eventId || msg?.id || `m-${ts}-${msg?.senderId || 'u'}`,
      item: msg,
    });
  }

  return out;
}

/**
 * CenteredLoader Component
 * Displays a centered loading spinner with optional text
 * @param {Object} props - Component props
 * @param {number} [props.size=32] - Size of the loader
 * @param {string} [props.text] - Optional text to display below the loader
 * @returns {JSX.Element} Centered loader component
 */
export const CenteredLoader = React.memo(({ size = 32, text }) => {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center p-4 cm_chat_loader">
      <Loader type="Oval" color="#F334BE" height={size} width={size} />
      <span className="loader-title mt-3 mb-0">{text}</span>
    </div>
  );
});

/**
 * Format timestamp to localized time string
 * @param {number} ts - Timestamp in milliseconds
 * @returns {string} Formatted time string (e.g., "2:30 PM")
 */
export function formatChatTime(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

/** Regex for tip line: "+$X tipped" at end of message */
export const TIP_MESSAGE_REGEX = /^\+\$[\d.]+\s+tipped$/;

/**
 * Normalize a Matrix message payload into a string.
 * Handles:
 * - Plain strings
 * - Event-style objects: { msgtype: "m.text", body: "<string or JSON string>" }
 * - Generic objects with a string `body` field
 * - Fallback to JSON.stringify for other objects
 *
 * @param {*} rawMessage
 * @returns {string}
 */
/**
 * Strip Matrix edit fallback prefix ("* " followed by JSON) so media/tip parsing works.
 * @param {string} s
 * @returns {string}
 */
function stripMatrixEditFallbackPrefix(s) {
  if (typeof s !== 'string') return s;
  const trimmed = s.trim();
  if (trimmed.startsWith('* ')) {
    const rest = trimmed.slice(2).trim();
    if (rest.startsWith('{')) return rest;
  }
  return trimmed || s;
}

export function normalizeMessageString(rawMessage) {
  if (typeof rawMessage === 'string') {
    return stripMatrixEditFallbackPrefix(rawMessage);
  }

  if (rawMessage && typeof rawMessage === 'object') {
    if (
      (rawMessage.msgtype === 'm.text' || rawMessage.type === 'm.text') &&
      typeof rawMessage.body === 'string'
    ) {
      return stripMatrixEditFallbackPrefix(rawMessage.body);
    }

    if (typeof rawMessage.body === 'string') {
      return stripMatrixEditFallbackPrefix(rawMessage.body);
    }

    try {
      return JSON.stringify(rawMessage);
    } catch {
      return '';
    }
  }

  return '';
}

export function parseTipJson(normalized) {
  if (typeof normalized !== 'string') return null;

  try {
    const obj = JSON.parse(normalized);

    if (!obj || typeof obj !== 'object') return null;

    const hasTipMeta =
      typeof obj.amount !== 'undefined' ||
      typeof obj.baseAmount !== 'undefined' ||
      typeof obj.realPrice !== 'undefined' ||
      typeof obj.tip_type !== 'undefined';

    return hasTipMeta ? obj : null;
  } catch {
    return null;
  }
};

/**
 * Pending tip display amount by user type.
 * Creator → otherUser.total (earnings); subscriber → otherUser.pendingTips (charged).
 * When metadata is 0, falls back to tip JSON on the last message body if present.
 *
 * @param {boolean} isCreator
 * @param {{ total?: number, pendingTips?: number }|null|undefined} otherUser
 * @param {string} [lastMessageBody]
 * @returns {number}
 */
export function getPendingTipsForUserType(isCreator, otherUser, lastMessageBody = '') {
  const fromMeta = Number(isCreator ? otherUser?.total : otherUser?.pendingTips);
  let pendingTips = Number.isFinite(fromMeta) && fromMeta > 0 ? fromMeta : 0;

  if (pendingTips === 0 && lastMessageBody) {
    const tipJson = parseTipJson(lastMessageBody);
    if (tipJson) {
      const raw = isCreator
        ? tipJson.realPrice ?? tipJson.baseAmount ?? tipJson.amount
        : tipJson.amount ?? tipJson.baseAmount ?? tipJson.realPrice;
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) pendingTips = n;
    }
  }

  return pendingTips;
}

/**
 * Parse message string as media JSON (type "media" with assets array).
 * @param {string} normalized - Normalized message string (may be JSON)
 * @returns {Object|null} Parsed media object or null
 */
export function parseMediaJson(normalized) {
  if (typeof normalized !== 'string') return null;

  try {
    const obj = JSON.parse(normalized);
    if (!obj || typeof obj !== 'object') return null;

    const isMediaType =
      obj.type === 'media' ||
      obj.type === 'm.media';

    if (!isMediaType || !Array.isArray(obj.assets) || obj.assets.length === 0) {
      return null;
    }

    return obj;
  } catch {
    return null;
  }
}

/**
 * Skip mail/REST notify for Matrix non-plain payloads (structured tips, media, JSON blobs).
 * @param {string} outboundBody - Argument passed to Matrix send
 * @returns {boolean}
 */
export function shouldSkipPrioritySubscriberNotify(outboundBody) {
  if (typeof outboundBody !== 'string') return true;
  const t = outboundBody.trim();
  if (!t) return true;
  if (parseTipJson(t)) return true;
  if (parseMediaJson(t)) return true;

  try {
    const parsed = JSON.parse(t);
    if (parsed !== null && typeof parsed === 'object') return true;
  } catch {
    /* plain text */
  }
  return false;
}

/**
 * Detect unanswered-call notice messages (m.notice) for dedicated chat UI.
 * Outgoing caller posts "Voice Call" / "Video Call"; legacy notices used "Missed … Call".
 * Accepts legacy "Missed audio call" (same as voice).
 *
 * @param {string} normalized - Output of normalizeMessageString
 * @returns {{ media: 'audio' | 'video' } | null}
 */
export function parseMissedCallNotice(normalized) {
  if (typeof normalized !== 'string') return null;
  const s = normalized.trim();
  const missed = /^Missed (audio|voice|video) call$/i.exec(s);
  if (missed) {
    const kind = missed[1].toLowerCase();
    const media = kind === 'video' ? 'video' : 'audio';
    return { media };
  }
  const plain = /^(voice|video) call$/i.exec(s);
  if (!plain) return null;
  const media = plain[1].toLowerCase() === 'video' ? 'video' : 'audio';
  return { media };
}

/**
 * Human-readable label for unanswered-call notices (chat thread + conversation list).
 * @param {{ media: 'audio' | 'video' }} missedCallInfo
 * @param {boolean} isSentByMe - True when the logged-in user posted the notice (caller).
 * @returns {string}
 */
export function formatMissedCallNoticeLabel(missedCallInfo, isSentByMe) {
  const callTypeLabel =
    missedCallInfo?.media === 'video' ? 'Video Call' : 'Voice Call';
  return isSentByMe ? `Outgoing ${callTypeLabel}` : `Missed ${callTypeLabel}`;
}

/**
 * Detect ended-call timeline notices from the caller (e.g. "Ended voice call - duration 01:22").
 *
 * @param {string} normalized - Output of normalizeMessageString
 * @returns {{ media: 'audio' | 'video', text: string, title: string, duration: string } | null}
 * text: "Voice Call - 01:08" / "Video Call - 00:25" (legacy consumers)
 */
export function parseEndedCallNotice(normalized) {
  if (typeof normalized !== 'string') return null;
  const s = normalized.trim();
  const m = /^Ended (voice|video) call - duration (\d+:\d{2})$/i.exec(s);
  if (!m) return null;
  const kind = m[1].toLowerCase();
  const media = kind === 'video' ? 'video' : 'audio';
  const duration = m[2];
  const title = media === 'video' ? 'Video Call Ended' : 'Voice Call Ended';
  const text = media === 'video' ? `Video Call - ${duration}` : `Voice Call - ${duration}`;
  return { media, text, title, duration };
}

/**
 * Format seconds as "m:ss" for preview (e.g. 10 -> "0:10").
 * @param {number} seconds
 * @returns {string}
 */
function formatDurationSeconds(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return '';
  const s = Math.floor(Number(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

/**
 * Get a short preview label for a parsed media message (e.g. "1 image", "2 videos").
 * @param {Object} parsedMediaJson - Result of parseMediaJson
 * @returns {string} Label like "1 image", "2 images", "1 video"
 */
export function getMediaPreviewLabel(parsedMediaJson) {
  const info = getMediaPreviewInfo(parsedMediaJson);
  return info ? info.label : '';
}

/**
 * Get preview label, icon type, and optional duration for a parsed media message.
 * Used to show the correct icon and text (e.g. "1 image", "1 video · 0:10", "Bundle · 1 image, 1 video").
 * @param {Object} parsedMediaJson - Result of parseMediaJson
 * @returns {{ label: string, iconType: 'image'|'video'|'bundle'|'file', durationText: string }} Or null if invalid
 */
export function getMediaPreviewInfo(parsedMediaJson) {
  if (!parsedMediaJson || !Array.isArray(parsedMediaJson.assets) || parsedMediaJson.assets.length === 0) {
    return null;
  }

  const assets = parsedMediaJson.assets;
  const imageCount = assets.filter((a) => (a?.type || '').toLowerCase() === 'image').length;
  const videoCount = assets.filter((a) => (a?.type || '').toLowerCase() === 'video').length;
  const otherCount = assets.length - imageCount - videoCount;

  const isBundle = assets.length > 1;
  const singleVideoDuration =
    !isBundle && videoCount === 1
      ? assets.find((a) => (a?.type || '').toLowerCase() === 'video')?.duration
      : null;
  const durationText = formatDurationSeconds(singleVideoDuration) || '';

  const parts = [];
  if (imageCount > 0) {
    parts.push(imageCount === 1 ? '1 image' : `${imageCount} images`);
  }
  if (videoCount > 0) {
    parts.push(videoCount === 1 ? '1 video' : `${videoCount} videos`);
  }
  if (otherCount > 0) {
    parts.push(otherCount === 1 ? '1 file' : `${otherCount} files`);
  }

  const baseLabel = parts.length ? parts.join(', ') : 'Media';

  let label = baseLabel;
  if (isBundle) {
    label = `Bundle · ${baseLabel}`;
  } else if (durationText) {
    label = `${baseLabel} · ${durationText}`;
  }

  let iconType = 'file';
  if (isBundle) {
    iconType = 'bundle';
  } else if (imageCount && !videoCount && !otherCount) {
    iconType = 'image';
  } else if (videoCount && !imageCount && !otherCount) {
    iconType = 'video';
  } else if (otherCount) {
    iconType = 'file';
  }

  return { label, iconType, durationText };
}

/**
 * Returns [mainText, tipLine] for legacy combined messages or JSON-based tip payloads.
 * - For JSON string payloads: treat `message` as main text and synthesize "+$X tipped".
 * - For plain text: if message is "text\n+$X tipped", split into [text, "+$X tipped"].
 * @param {string} message - Full message text
 * @returns {[string, string|null]} [mainText, tipLine or null]
 */
export function splitCombinedMessage(message) {
  if (!message || typeof message !== 'string') return [message || '', null];

  // New JSON-based message tip format
  try {
    const obj = JSON.parse(message);
    if (obj && typeof obj === 'object') {
      const main = typeof obj.message === 'string' ? obj.message : '';
      const rawAmount = obj.amount ?? obj.baseAmount ?? obj.realPrice;
      if (rawAmount != null) {
        const num = Number(rawAmount);
        if (Number.isFinite(num)) {
          const label = num === Math.trunc(num) ? String(Math.trunc(num)) : num.toFixed(2);
          return [main, `+$${label} tipped`];
        }
      }
      return [main || message, null];
    }
  } catch {
    // Not JSON – fall through to legacy format
  }

  // Legacy "text\n+$X tipped" format
  const parts = message.split(/\n/);
  const last = parts[parts.length - 1]?.trim();
  if (parts.length >= 2 && last && TIP_MESSAGE_REGEX.test(last)) {
    const mainText = parts.slice(0, -1).join('\n').trim();
    return [mainText, last];
  }
  return [message, null];
}

const URL_REGEX = /((https?:\/\/|www\.)[^\s]+)/gi;
const TRAILING_PUNCTUATION = '.,!?)]}:;\'"';

/**
 * Split URL text from trailing punctuation
 * @param {string} urlText - URL text that may have trailing punctuation
 * @returns {Array} [urlText, trailingPunctuation]
 */
function splitTrailingPunctuation(urlText) {
  let end = urlText.length;
  while (end > 0 && TRAILING_PUNCTUATION.includes(urlText[end - 1])) {
    end -= 1;
  }
  return [urlText.slice(0, end), urlText.slice(end)];
}

/**
 * Render text with clickable links
 * Converts URLs in text to clickable anchor tags
 * @param {string} text - Text that may contain URLs
 * @returns {string|Array} Original text if no links found, or array of React nodes with links
 */
export function renderTextWithLinks(text) {
  if (!text) return text;

  // `URL_REGEX` is global (`/g`) so we must reset it between calls.
  URL_REGEX.lastIndex = 0;

  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  let hasLink = false;

  while ((match = URL_REGEX.exec(text)) !== null) {
    const rawMatch = match[0];
    const matchIndex = match.index;

    if (matchIndex > lastIndex) {
      nodes.push(text.slice(lastIndex, matchIndex));
    }

    const [urlText, trailing] = splitTrailingPunctuation(rawMatch);
    const href = urlText.startsWith('http') ? urlText : `https://${urlText}`;

    hasLink = true;
    nodes.push(
      <a
        key={`link-${key++}`}
        className="chat_link"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {urlText}
      </a>
    );

    if (trailing) nodes.push(trailing);

    lastIndex = matchIndex + rawMatch.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  // If no links were found, return the original string to avoid extra React nodes.
  return hasLink ? nodes : text;
}

export function getPreviewData(rawMessage, isCreator, isLastMessageSentByMe = false) {
  const normalized = normalizeMessageString(rawMessage);

  if (!normalized) {
    return {
      message: 'No messages yet',
      tipLine: null,
      isMediaPreview: false,
      mediaIconType: null,
    };
  }

  const missedCallInfo = parseMissedCallNotice(normalized);
  if (missedCallInfo) {
    return {
      message: formatMissedCallNoticeLabel(missedCallInfo, isLastMessageSentByMe),
      tipLine: null,
      isMediaPreview: false,
      mediaIconType: null,
    };
  }

  const endedCallInfo = parseEndedCallNotice(normalized);
  if (endedCallInfo) {
    return {
      message: endedCallInfo.title,
      tipLine: null,
      isMediaPreview: false,
      mediaIconType: null,
    };
  }

  const mediaJson = parseMediaJson(normalized);

  if (mediaJson) {
    const mediaInfo = getMediaPreviewInfo(mediaJson);

    return {
      message: mediaInfo?.label || 'Media',
      tipLine: null,
      isMediaPreview: true,
      mediaIconType: mediaInfo?.iconType ?? null,
    };
  }

  const tipJson = parseTipJson(normalized);

  if (tipJson) {
    const mainText =
      typeof tipJson.message === 'string' ? tipJson.message : '';

    const rawAmount = isCreator
      ? tipJson.realPrice ?? tipJson.amount ?? tipJson.baseAmount
      : tipJson.amount ?? tipJson.baseAmount ?? tipJson.realPrice;

    const formatted = formatTipAmount(rawAmount);
    const tip = formatted ? `+$${formatted} tipped` : null;

    if (!mainText && tip) {
      return {
        message: tip,
        tipLine: null,
        isMediaPreview: false,
        mediaIconType: null,
      };
    }

    return {
      message: mainText || 'No messages yet',
      tipLine: tip,
      isMediaPreview: false,
      mediaIconType: null,
    };
  }

  const [mainText, tip] = splitCombinedMessage(normalized);

  return {
    message: mainText || tip || 'No messages yet',
    tipLine: tip || null,
    isMediaPreview: false,
    mediaIconType: null,
  };
}